import {
  Stack,
  Text,
  Icon,
  Badge,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  Box,
} from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthAlert } from '@/services/api/services-api';
import {
  SEVERITY_COLOR,
  useEnvHealth,
  useWorkspaceHealthSimulation,
  DATA_ALERT_SEVERITY_RANK,
} from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import * as styles from './index.css';

function collapseProcessingAlerts(alerts: ServicesApiEnvHealthAlert[]): ServicesApiEnvHealthAlert[] {
  const processingAlerts = alerts.filter((a) => a.action_type === 'processing_details');
  const otherAlerts = alerts.filter((a) => a.action_type !== 'processing_details');

  if (processingAlerts.length <= 2) {
    return alerts.toSorted((a, b) => DATA_ALERT_SEVERITY_RANK[a.severity] - DATA_ALERT_SEVERITY_RANK[b.severity]);
  }

  const highestSeverity = processingAlerts.some((a) => a.severity === 'error') ? 'error' : 'caution';

  const collapsed: ServicesApiEnvHealthAlert = {
    severity: highestSeverity,
    title: 'Multiple queues behind',
    message: 'Some data and messages may be delayed',
    action_label: 'View details',
    action_type: 'processing_details',
  };

  return [collapsed, ...otherAlerts].sort(
    (a, b) => DATA_ALERT_SEVERITY_RANK[a.severity] - DATA_ALERT_SEVERITY_RANK[b.severity],
  );
}

export function AlertsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data?.body;
  const alerts = data != null ? collapseProcessingAlerts(data.alerts) : [];
  const hasAlerts = alerts.length > 0;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <InlineStack gap="050" alignBlock="center">
            Active issues
            {hasAlerts ? <Badge isEmphasized={true}>{data?.alerts.length}</Badge> : null}
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection withPadding={false}>
        {hasAlerts ? (
          <Stack px="300">
            {alerts.slice(0, 5).map((alert, i) => (
              <Stack
                key={i}
                gap="050"
                pt={i == 0 ? undefined : '200'}
                pb={i == alerts.slice(0, 5).length - 1 ? '300' : '200'}
                className={styles.categoryGroup}
              >
                <InlineStack gap="100" alignBlock="center">
                  <StatusIndicator color={SEVERITY_COLOR[alert.severity]} />
                  <Text size="s" weight="semibold">
                    {alert.title}
                  </Text>
                </InlineStack>
                <Text size="xs" color="subtle">
                  {alert.message}
                </Text>
                {alert.action_type === 'failed_events' ? (
                  <Box>
                    <Link href={`/workspaces/${workspaceId}/health?drawer=data-alerts&tab=data`} variant="secondary">
                      {alert.action_label}
                    </Link>
                  </Box>
                ) : null}
              </Stack>
            ))}
          </Stack>
        ) : (
          <InlineStack gap="100" alignBlock="center" px="300" pb="300">
            <Icon name="check-circle" size="sm" color="success" />
            <Text size="s" color="subtle">
              All clear — nothing needs your attention
            </Text>
          </InlineStack>
        )}
      </PanelSection>
    </Panel>
  );
}
